/**
 * BleTransport — abstract transport contract for the mesh layer.
 *
 * Concrete transports (SimulatedTransport, WebBluetoothTransport, future
 * CapacitorTransport) implement this interface. MeshClient talks only to
 * this interface, so swapping transports never touches the protocol or UI
 * layer.
 *
 * Capability flags describe what a transport can actually do:
 *   canScan       — can discover peers
 *   canAdvertise  — can make itself discoverable to peers
 *   canRelay      — can forward packets for other peers (gossip)
 *
 * For reference:
 *   SimulatedTransport   → canScan:true,  canAdvertise:true,  canRelay:true
 *   WebBluetoothTransport→ canScan:true,  canAdvertise:false, canRelay:false
 *   CapacitorTransport   → canScan:true,  canAdvertise:true,  canRelay:true
 */

/**
 * @typedef {Object} Peer
 * @property {Uint8Array} id        8-byte peer ID
 * @property {string} idHex         hex form of id
 * @property {string} nickname      self-asserted display name
 * @property {number} lastSeen      timestamp (ms) of most recent contact
 * @property {number} rssi          last signal strength, if known (-dBm)
 * @property {string} transport     name of the transport that saw the peer
 */

/**
 * @typedef {Object} BleCapabilities
 * @property {boolean} canScan
 * @property {boolean} canAdvertise
 * @property {boolean} canRelay
 * @property {string}  name         human-readable transport name
 */

/**
 * @typedef {Object} TransportHandlers
 * @property {(peer: Peer) => void} onPeerSeen       called on every announce/contact
 * @property {(peerId: Uint8Array) => void} onPeerLost  called when a peer's lastSeen ages out
 * @property {(packetBytes: Uint8Array, fromPeerId: Uint8Array|null) => void} onPacket
 */

export class BleTransport {
  /**
   * @param {Object} opts
   * @param {import('../Identity.js').loadIdentity extends infer _ ? any : any} opts.identity
   */
  constructor(opts) {
    this.identity = opts.identity;
    // onNeedAnnounce is invoked by peripheral-capable transports when a new
    // central subscribes, so MeshClient can (re)send a signed announce that
    // binds our sender ID on that link. Central-only transports never call it.
    this.handlers = { onPeerSeen: noop, onPeerLost: noop, onPacket: noop, onNeedAnnounce: noop };
    this.started = false;
  }

  /** @returns {BleCapabilities} */
  get capabilities() {
    throw new Error('BleTransport.capabilities must be overridden');
  }

  /** Register handler callbacks. */
  setHandlers(handlers) {
    this.handlers = { ...this.handlers, ...handlers };
  }

  /**
   * Start the transport (discovery, advertising, etc).
   */
  async start() { throw new Error('BleTransport.start must be overridden'); }

  /**
   * Stop the transport and release any held resources.
   */
  async stop()  { throw new Error('BleTransport.stop must be overridden'); }

  /**
   * Deliver a packet.
   *   - If recipientId is null or the broadcast ID, the transport should
   *     flood the packet to every peer it can reach.
   *   - Otherwise, the transport should attempt to reach the specific peer
   *     (directly or via relays, if supported).
   *
   * @param {Uint8Array} packetBytes
   * @param {Uint8Array | null} [recipientId]
   */
  async send(packetBytes, recipientId = null) {
    void packetBytes; void recipientId;
    throw new Error('BleTransport.send must be overridden');
  }
}

function noop() {}

export default BleTransport;
